/**
 * Shared partner-note detail renderer + right-panel payload builder.
 *
 * ONE renderer for the note-detail panel, consumed by BOTH surfaces that open
 * it: `/notes` (PartnerNotesSection) and the federated `/search` page
 * (SessionsSection). Neither owns a copy of the note-detail markup — this is
 * the single source of truth (DRY), so the two surfaces can't drift.
 *
 * The module is instance-free: every surface-specific behaviour (partner name,
 * own name, the Discuss/Retract/Star/Archive/Delete actions) is injected as
 * data or a callback. That keeps the render pure and portable — a raw memo row
 * plus a few strings/callbacks is all it needs.
 *
 * @module components/partnerNotesDrillHelper
 */

import { escapeHtml } from '../core/utils.js';
import { copyToClipboard, shareOrCopy } from '../utils/shareContent.js';
import { renderNavCard } from './navCard.js';

/** Max words of a derived detail title for legacy rows without a stored title. */
const DERIVED_TITLE_WORDS = 6;

/** Reply-arrow glyph shown on a note that answers an earlier one (reply-pair linkage). */
const REPLY_GLYPH = '<span class="note-detail__reply-glyph" aria-hidden="true">↳</span>';

/**
 * Format an ISO date string as "April 19, 2026". Empty string on bad input.
 * @param {string} iso - ISO date string
 * @returns {string} Long-form date or ''
 */
export function formatLongDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Note title: the stored agent-authored title, or (legacy rows without one)
 * the first few words of the prose. Shared by the row renderer and the detail
 * panel so both surfaces title a note identically.
 * @param {Object} note - Note row
 * @returns {string} Display title (unescaped)
 */
export function noteCardTitle(note) {
  const stored = typeof note?.title === 'string' ? note.title.trim() : '';
  if (stored) return stored;
  const words = String(note?.content || '').replace(/\s+/g, ' ').trim().split(' ');
  if (!words[0]) return 'Note';
  const head = words.slice(0, DERIVED_TITLE_WORDS).join(' ');
  return words.length > DERIVED_TITLE_WORDS ? `${head}…` : head;
}

/**
 * One partner-note nav card for a page that links out to it — session detail's
 * Notes zone. Sent notes only (a note reaches a session note through its ORIGIN
 * session, and that is always the sender's), so the rail reports when it went, or
 * that it was withdrawn.
 *
 * Visual only; the caller wires its own click delegation on `data-note-id`.
 *
 * @param {Object} note - Sender-view note row
 * @returns {string} HTML string
 */
export function renderNoteNavCard(note) {
  // Short form, matching the experiment rail beside it — a right rail reports a
  // fact in passing, and the zones are meant to read as one family. (The note's
  // own detail panel still spells the date out in full.) `sentAt` is nullable on
  // legacy rows, so fall back rather than print an invalid date — the same order
  // `normalizeMemo` reads them in.
  const stamp = new Date(note?.sentAt || note?.createdAt || '');
  const when = Number.isNaN(stamp.getTime())
    ? ''
    : stamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const railHtml = note?.revokedAt
    ? '<span class="note-row__status"><span class="tag">Withdrawn</span></span>'
    : (when ? `<span class="note-row__status"><span class="note-row__date">Sent ${escapeHtml(when)}</span></span>` : '');

  return renderNavCard({
    title: noteCardTitle(note),
    dataAttrs: { 'data-note-id': String(note?.id ?? '') },
    cardClass: 'note-card',
    snippet: String(note?.content || '').replace(/\s+/g, ' '),
    railHtml
  });
}

/**
 * Format a note's content as shareable plain text: title, prose paragraphs,
 * and the derived sign-off — mirrors `renderNoteDetailBody`'s structure but
 * without the `escapeHtml()` step that split feeds through for HTML display.
 * @param {Object} note - Note row
 * @param {Object} opts
 * @param {'received'|'sent'} opts.list - Which list the note belongs to
 * @param {string} opts.partnerName - Linked partner's display name
 * @param {string} opts.ownName - Current user's display name
 * @returns {string} Plain text
 */
export function formatPartnerNoteForSharing(note, { list, partnerName, ownName }) {
  const title = noteCardTitle(note);
  const paragraphs = String(note?.content || '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  const signOffName = list === 'received' ? partnerName : ownName;
  return [title, '', paragraphs.join('\n\n'), '', `— ${signOffName}`].join('\n');
}

/**
 * Render the detail panel body in the unified detail-header order: the note's
 * own **title** (H1, shared `.sd-title`), the Sent/Received-prefixed date meta
 * in the subtitle slot (shared `.sd-subtitle`) with the Withdrawn state as a
 * `.tag` pill beside it (sent list only), the reply-pair linkage chrome, the
 * serif prose, a display-only sign-off, and the hidden Retract confirm block
 * (sent list, not yet withdrawn) that the header "Retract Note" button reveals.
 *
 * The sign-off ("— Laurel" / "— Doug") is UI chrome, not stored content — it's
 * derived per render from the partner/own display name, same as the header's
 * "Note to/from X" label. Nothing is prepended or appended to the persisted
 * `content` (see the salutation/sign-off decision in compose-memo-prompt.md).
 *
 * @param {Object} note - Note row
 * @param {Object} opts
 * @param {'received'|'sent'} opts.list - Which list the note belongs to
 * @param {string} opts.partnerName - Linked partner's display name (resolved)
 * @param {string} opts.ownName - Current user's display name (resolved)
 * @returns {string} HTML string
 */
export function renderNoteDetailBody(note, { list, partnerName, ownName }) {
  const when = formatLongDate(note?.sentAt || note?.createdAt);
  const metaLabel = list === 'sent' ? 'Sent' : 'Received';

  // Withdrawn state rides the meta row as a quiet tag pill rather than its own
  // status line — state chrome stays inside the unified header block.
  const withdrawnDate = list === 'sent' && note?.revokedAt ? formatLongDate(note.revokedAt) : '';
  const status = list === 'sent' && note?.revokedAt
    ? `<span class="tag note-detail__status">Withdrawn${withdrawnDate ? ` · ${escapeHtml(withdrawnDate)}` : ''}</span>`
    : '';

  const meta = when || status
    ? `<p class="sd-subtitle note-detail__meta">${when ? `<span class="note-detail__meta-date">${metaLabel} · ${escapeHtml(when)}</span>` : ''}${status}</p>`
    : '';

  // Reply-pair linkage: a note that answers an earlier one shows what it replies
  // to (gated on parentTitle truthiness — a titleless legacy parent renders nothing).
  // Two-way linkage (mutually exclusive — replies are terminal, so a note is a
  // root xor a reply): a reply names the note it answers; a note that HAS a reply
  // names it forward. Both links open the linked note in the panel (no list switch).
  const replyContext = note?.parentTitle
    ? `<p class="note-detail__reply-context">${REPLY_GLYPH} Replying to <a class="note-detail__reply-link" data-action="open-linked-note" data-note-id="${escapeHtml(note.parentMemoId || '')}" role="button" tabindex="0">“${escapeHtml(note.parentTitle)}”</a></p>`
    : '';
  const replyToContext = note?.replyTitle
    ? `<p class="note-detail__reply-context">${REPLY_GLYPH} Reply · <a class="note-detail__reply-link" data-action="open-linked-note" data-note-id="${escapeHtml(note.replyMemoId || '')}" role="button" tabindex="0">“${escapeHtml(note.replyTitle)}”</a></p>`
    : '';

  const title = `<h1 class="sd-title note-detail__title">${escapeHtml(noteCardTitle(note))}</h1>`;

  const paragraphs = String(note?.content || '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p class="partner-note__text">${escapeHtml(p)}</p>`)
    .join('');

  const name = escapeHtml(partnerName);
  const signOffName = escapeHtml(list === 'received' ? partnerName : ownName);
  const signOff = `<p class="note-detail__signoff">— ${signOffName}</p>`;

  const retract = list === 'sent' && !note?.revokedAt
    ? `
      <div class="note-detail__actions" hidden>
        <div class="note-detail__confirm">
          <p class="note-detail__confirm-text">This removes the note from ${name}’s view. They may have already read it.</p>
          <div class="note-detail__confirm-actions">
            <button type="button" class="note-detail__confirm-cancel" data-action="cancel-retract">Cancel</button>
            <button type="button" class="note-detail__confirm-retract" data-action="confirm-retract">Withdraw note</button>
          </div>
        </div>
      </div>
    `
    : '';

  return `
    ${title}
    ${meta}
    ${replyContext}
    ${replyToContext}
    <div class="note-detail__prose">${paragraphs}</div>
    ${signOff}
    ${retract}
  `;
}

/**
 * Reveal the inline Retract confirm block (driven by the panel-header
 * "Retract Note" button).
 * @param {HTMLElement} content - Detail content element
 */
export function showRetractConfirm(content) {
  const actions = content.querySelector('.note-detail__actions');
  if (actions) actions.hidden = false;
}

/**
 * Wire the inline confirm → revoke flow on a sent-note detail. Cancel just
 * hides the block; Withdraw disables both buttons and awaits the injected
 * `onRetract` (which owns the actual revoke + surface-specific refresh). On a
 * retryable failure `onRetract` rejects and the buttons re-enable; on success
 * or a handled terminal case (e.g. 409) it resolves and the caller has already
 * closed/refreshed the panel.
 * @param {HTMLElement} content - Detail content element (pre-mount)
 * @param {() => Promise<void>} onRetract - Revoke + refresh, injected per surface
 */
function attachRetractConfirm(content, onRetract) {
  const actions = content.querySelector('.note-detail__actions');
  const cancelBtn = content.querySelector('[data-action="cancel-retract"]');
  const confirmBtn = content.querySelector('[data-action="confirm-retract"]');
  if (!actions || !cancelBtn || !confirmBtn) return;

  cancelBtn.addEventListener('click', () => {
    actions.hidden = true;
  });
  confirmBtn.addEventListener('click', async () => {
    confirmBtn.disabled = true;
    cancelBtn.disabled = true;
    try {
      await onRetract();
    } catch {
      // Retryable failure — the caller surfaced its own error; re-enable so the
      // user can try again. (On a terminal case the caller resolves instead.)
      confirmBtn.disabled = false;
      cancelBtn.disabled = false;
    }
  });
}

/**
 * Build the `right-panel:open` payload for a note. The panel header carries no
 * title text — the note's own title renders in the body. The header primary
 * button is the note's action: received → "Discuss Note", sent-and-not-
 * withdrawn → "Retract Note" (reveals the inline confirm). Received notes also
 * get a header `starButton` (left of Options) — starring is recipient-side
 * only, so sent notes get none. The Options dropdown offers Copy, Share,
 * Archive/Unarchive, and Delete — Copy/Share are handled locally (all their
 * inputs are already in scope), Archive/Delete pass through to the injected
 * `onOption` callback so this stays instance-free.
 *
 * @param {Object} note - Raw memo row (has `title`, `content`, dates, star/archive/revoke stamps)
 * @param {Object} opts
 * @param {'received'|'sent'} opts.list - Which list the note belongs to
 * @param {string} opts.partnerName - Linked partner's display name
 * @param {string} opts.ownName - Current user's display name
 * @param {string} opts.contentType - Right-panel contentType (surface-owned; the
 *   owning section's `right-panel:closed` listener filters on this)
 * @param {() => void} opts.onDiscuss - Received-note "Discuss Note" handler
 * @param {(nextStarred: boolean) => boolean|Promise<boolean>} [opts.onStar] - Received-note
 *   header star toggle; resolves whether the toggle succeeded (RightPanelManager
 *   optimistically flips and reverts on a falsy result)
 * @param {() => Promise<void>} opts.onRetract - Sent-note revoke + refresh handler
 * @param {(noteId: string) => void} [opts.onOpenLinkedNote] - Handler for the two-way
 *   reply links ("Replying to …" / "Reply · …"); opens the linked note in the panel.
 *   Omitted surfaces (e.g. /search) render the links inert.
 * @param {(action: 'archive'|'delete') => void} opts.onOption - Options-dropdown handler
 * @returns {Object} right-panel payload
 */
export function buildNoteDetailPayload(note, {
  list, partnerName, ownName, contentType,
  onDiscuss, onStar, onRetract, onOpenLinkedNote, onOption
}) {
  const content = document.createElement('div');
  content.className = 'note-detail';
  content.innerHTML = renderNoteDetailBody(note, { list, partnerName, ownName });

  if (list === 'sent' && !note.revokedAt) {
    attachRetractConfirm(content, onRetract);
  }

  // Two-way reply linkage: each "Replying to …" / "Reply · …" link opens the linked
  // note in the same panel (no list switch). Only wired when the surface supplies a
  // handler (each link carries its target in data-note-id).
  if (onOpenLinkedNote) {
    content.querySelectorAll('[data-action="open-linked-note"]').forEach((link) => {
      const linkedId = link.getAttribute('data-note-id');
      if (!linkedId) return;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        onOpenLinkedNote(linkedId);
      });
    });
  }

  let primaryButton = null;
  if (list === 'received') {
    primaryButton = { label: 'Discuss Note', onClick: onDiscuss };
  } else if (!note.revokedAt) {
    primaryButton = { label: 'Retract Note', onClick: () => showRetractConfirm(content) };
  }

  // Received notes get a header star toggle (recipient-side only); sent get none.
  const starButton = list === 'received'
    ? { starred: !!note.starredAt, onClick: onStar }
    : null;

  const optionItems = [];
  optionItems.push({ action: 'copy', label: 'Copy' });
  optionItems.push({ action: 'share', label: 'Share' });
  optionItems.push({ action: 'archive', label: note.archivedAt ? 'Unarchive' : 'Archive' });
  optionItems.push({ action: 'delete', label: 'Delete', variant: 'danger' });

  // Copy/Share are handled directly here — note/list/partnerName/ownName are
  // already synchronously in scope, no injected callback needed. Archive/
  // delete continue to pass through to the caller's existing onOption.
  const handleOption = async (action) => {
    if (action === 'copy' || action === 'share') {
      try {
        const text = formatPartnerNoteForSharing(note, { list, partnerName, ownName });
        if (action === 'copy') await copyToClipboard(text, { successMessage: 'Note copied' });
        else await shareOrCopy({ title: noteCardTitle(note), text });
      } catch (error) {
        console.error('[partnerNotesDrillHelper] Copy/share failed:', error);
        window.notificationManager?.showError('Something went wrong. Please try again.');
      }
      return;
    }
    return onOption(action);
  };

  return {
    title: '',
    titleAlign: 'left',
    content,
    contentType,
    starButton,
    optionsDropdown: {
      items: optionItems,
      onAction: handleOption
    },
    primaryButton
  };
}
